import { useEffect, useRef } from 'react';

const WebGLBackground = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl', { 
      antialias: false, 
      alpha: true,
      powerPreference: 'low-power'
    });
    
    if (!gl) {
      console.warn('WebGL not supported');
      return;
    }

    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 a_position;
      void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
      }
    `;

    // Fragment shader with domain-warped FBM
    const fragmentShaderSource = `
      precision mediump float;
      
      uniform vec2 u_resolution;
      uniform float u_time;
      uniform vec2 u_mouse;
      
      // Site color palette
      const vec3 COLOR_VIOLET = vec3(0.545, 0.361, 0.965);  // #8B5CF6
      const vec3 COLOR_CYAN = vec3(0.024, 0.714, 0.831);    // #06B6D4
      const vec3 COLOR_GREEN = vec3(0.063, 0.725, 0.506);   // #10B981
      const vec3 COLOR_BG = vec3(0.059, 0.090, 0.165);      // #0F172A
      
      // Hash function for noise
      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }
      
      // Smooth noise
      float noise(vec2 p) {
        vec2 i = floor(p);
        vec2 f = fract(p);
        f = f * f * (3.0 - 2.0 * f);
        
        float a = hash(i);
        float b = hash(i + vec2(1.0, 0.0));
        float c = hash(i + vec2(0.0, 1.0));
        float d = hash(i + vec2(1.0, 1.0));
        
        return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
      }
      
      // Fractal Brownian Motion
      float fbm(vec2 p) {
        float value = 0.0;
        float amplitude = 0.5;
        float frequency = 1.0;
        
        for (int i = 0; i < 5; i++) {
          value += amplitude * noise(p * frequency);
          amplitude *= 0.5;
          frequency *= 2.0;
        }
        
        return value;
      }
      
      // Domain warping
      float warpedFbm(vec2 p, float time) {
        vec2 q = vec2(
          fbm(p + vec2(0.0, 0.0)),
          fbm(p + vec2(5.2, 1.3))
        );
        
        vec2 r = vec2(
          fbm(p + 4.0 * q + vec2(1.7, 9.2) + 0.15 * time),
          fbm(p + 4.0 * q + vec2(8.3, 2.8) + 0.126 * time)
        );
        
        return fbm(p + 4.0 * r);
      }
      
      void main() {
        vec2 uv = gl_FragCoord.xy / u_resolution;
        vec2 p = uv * 3.0;
        
        // Subtle mouse influence
        vec2 mouseOffset = (u_mouse - 0.5) * 0.3;
        p += mouseOffset;
        
        // Slow time for organic movement
        float time = u_time * 0.08;
        
        // Get warped FBM value
        float f = warpedFbm(p, time);
        
        // Create color gradient based on FBM
        vec3 color = COLOR_BG;
        
        // Layer colors based on FBM value
        float t1 = smoothstep(0.3, 0.5, f);
        float t2 = smoothstep(0.5, 0.7, f);
        float t3 = smoothstep(0.6, 0.8, f);
        
        color = mix(color, COLOR_VIOLET * 0.15, t1 * 0.4);
        color = mix(color, COLOR_CYAN * 0.12, t2 * 0.3);
        color = mix(color, COLOR_GREEN * 0.1, t3 * 0.2);
        
        // Add subtle glow at mouse position
        float mouseDist = length(uv - u_mouse);
        float mouseGlow = exp(-mouseDist * 4.0) * 0.08;
        color += COLOR_VIOLET * mouseGlow;
        
        // Very subtle vignette
        float vignette = 1.0 - length(uv - 0.5) * 0.3;
        color *= vignette;
        
        gl_FragColor = vec4(color, 1.0);
      }
    `;

    // Compile shader
    const compileShader = (source, type) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.error('Shader compile error:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = compileShader(vertexShaderSource, gl.VERTEX_SHADER);
    const fragmentShader = compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER);

    if (!vertexShader || !fragmentShader) return;

    // Create program
    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);

    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.error('Program link error:', gl.getProgramInfoLog(program));
      return;
    }

    gl.useProgram(program);

    // Create fullscreen quad
    const positions = new Float32Array([
      -1, -1,
       1, -1,
      -1,  1,
       1,  1,
    ]);

    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    const positionLocation = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(positionLocation);
    gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const resolutionLocation = gl.getUniformLocation(program, 'u_resolution');
    const timeLocation = gl.getUniformLocation(program, 'u_time');
    const mouseLocation = gl.getUniformLocation(program, 'u_mouse');

    // Handle resize with DPR cap at 1.5
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(resolutionLocation, canvas.width, canvas.height);
    };

    resize();
    window.addEventListener('resize', resize);

    // Mouse tracking with smoothing
    const handleMouseMove = (e) => {
      mouseRef.current = {
        x: e.clientX / window.innerWidth,
        y: 1.0 - (e.clientY / window.innerHeight)
      };
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Animation loop
    let startTime = performance.now();
    let smoothMouse = { x: 0.5, y: 0.5 };

    const render = () => {
      const time = (performance.now() - startTime) / 1000;
      
      // Smooth mouse interpolation
      smoothMouse.x += (mouseRef.current.x - smoothMouse.x) * 0.05;
      smoothMouse.y += (mouseRef.current.y - smoothMouse.y) * 0.05;
      
      gl.uniform1f(timeLocation, time);
      gl.uniform2f(mouseLocation, smoothMouse.x, smoothMouse.y);
      
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      gl.deleteProgram(program);
      gl.deleteShader(vertexShader);
      gl.deleteShader(fragmentShader);
      gl.deleteBuffer(positionBuffer);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
      aria-hidden="true"
    />
  );
};

export default WebGLBackground;
